import React, { useEffect, useState, useRef } from 'react';
import {
    XMarkIcon,
    EyeIcon,
    EyeSlashIcon,
    KeyIcon,
    UserIcon,
    GlobeAltIcon,
    DocumentTextIcon,
    FolderIcon,
    HeartIcon,
    SparklesIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import { useVault } from '../../contexts/VaultContext';
import { ICreatePasswordEntry, IDecryptedPasswordEntry } from '../../types/vault.types';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { toast } from 'react-hot-toast';
import Input from '../common/Input';
import { decryptKeyData } from '../../utils/encryption';
import { useCategories } from '../../contexts/CategoryContext';
import { VaultService } from '../../services/vault.service';
import { CategoryService } from '../../services/category.service';
import { ICategory } from '../../types/category.types';
import { secureStore } from '../../utils/secureStore';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAppSelector } from '../../hooks/useRedux';
import { MultiSelect } from 'react-multi-select-component';

interface Option {
    label: string;
    value: string;
    disabled?: boolean;
    isShared?: boolean;
    sharedKey?: string;
}

interface EditPasswordModalProps {
    entry?: IDecryptedPasswordEntry;
    isOpen: boolean;
    onClose: () => void;
    isSharedPassword?: boolean;
    permissionLevel?: string;
    sharedKey?: string;
    onSuccessfulUpdate?: () => Promise<void> | void;
}

interface FormErrors {
    title?: string;
    username?: string;
    password?: string;
    website_url?: string;
    favourite?: boolean;
}

interface FormData {
    title: string;
    username: string;
    password: string;
    website_url: string;
    notes: string;
    favourite: boolean;
    category_id: string;
    category_ids: string[];
    primary_category_id: string;
    isSharedUpdate: boolean;
    sharedKey?: string;
    password_strength: string;
    auto_login: boolean;
    favicon_url: string;
    remove_category_ids: string[];
}

const calculatePasswordStrength = (password: string, email?: string): string => {
    const feedback: string[] = [];
    let score = 0;

    // Handle null or undefined password
    if (!password) {
        return 'Very Weak';
    }

    // Length check
    if (password.length >= 12) {
        score += 20;
    }

    // Number check
    if (/\d/.test(password)) {
        score += 20;
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
        score += 20;
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
        score += 20;
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        score += 20;
    }

    // Email check
    if (email && password.toLowerCase().includes(email.toLowerCase())) {
        score -= 20;
    }

    // Get strength label based on score
    if (score <= 20) return 'Very Weak';
    if (score <= 40) return 'Weak';
    if (score <= 60) return 'Fair';
    if (score <= 80) return 'Strong';
    return 'Very Strong';
};

const EditPasswordModal: React.FC<EditPasswordModalProps> = ({
    entry,
    isOpen,
    onClose,
    isSharedPassword = false,
    permissionLevel = 'read',
    onSuccessfulUpdate,
    sharedKey
}) => {
    const { updateEntry, refreshEntries } = useVault();
    const { categories, refreshCategories, createCategory } = useCategories();
    const { activeWorkspace } = useWorkspace();
    const { team_id, role_id } = useAppSelector(state => state.auth);

    // Add a ref to keep track of initial categories
    const initialCategoriesRef = useRef<string[]>([]);

    // Convert categories to options format for react-multi-select-component
    const categoryOptions: Option[] = categories.filter(cat => !(cat.permission_level === 'read')).map(cat => ({
        label: cat.name,
        value: cat.id,
        isShared: cat.is_shared,
        sharedKey: cat.shared_key
    }));

    // Initialize selected options from existing data
    const initializeSelectedOptions = (): Option[] => {
        if (entry?.categories && entry.categories.length > 0) {
            const validCategories = entry.categories.filter(entryCategory =>
                categories.some((userCategory: any) => userCategory.id === entryCategory.id)
            );
            return validCategories.map(cat => ({
                label: cat.name,
                value: cat.id,
                isShared: cat.is_shared,
                sharedKey: cat.shared_key
            }));
        } else if (entry?.category_id) {
            const category = categories.find(c => c.id === entry.category_id);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        }
        return [];
    };

    const [selectedOptions, setSelectedOptions] = useState<Option[]>(initializeSelectedOptions);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingCategoryLoading, setCreatingCategoryLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
        entry?.category_id ? categories.find(c => c.id === entry.category_id) || null : null
    );

    // Initialize formData with remove_category_ids
    const [formData, setFormData] = useState<FormData>({
        title: entry?.title || '',
        username: entry?.username || '',
        password: entry?.password || '',
        website_url: entry?.website_url || '',
        notes: entry?.notes || '',
        favourite: entry?.favourite || false,
        category_id: entry?.category_id || '',
        category_ids: [],
        primary_category_id: selectedCategory?.id || '',
        isSharedUpdate: isSharedPassword || !!entry?.shared_key,
        sharedKey: entry?.shared_key || undefined,
        auto_login: entry?.auto_login || false,
        password_strength: entry?.password_strength || '',
        favicon_url: entry?.favicon_url || '',
        remove_category_ids: [],
    });

    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [autoLogin, setAutoLogin] = useState(entry?.auto_login || false);
    const [isFormShared, setIsFormShared] = useState(isSharedPassword || !!entry?.shared_key);
    // Check if entry is V1 (crypto_version = 1) - disable editing for V1 passwords
    const isV1Password = entry?.crypto_version === 1;
    // const isReadOnly = !entry?.is_owner && isSharedPassword && entry?.permission_level === 'read' && !(role_id === 1 || role_id === 2);
    const isReadOnly = permissionLevel === 'read' || isV1Password;
    console.log("permissionLevel", permissionLevel);
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        if (formData.website_url) {
            const urlToTest = formData.website_url.trim();

            try {
                const parsedUrl = new URL(urlToTest);
                if (!/^https?:$/.test(parsedUrl.protocol)) {
                    newErrors.website_url = 'URL must start with http:// or https://';
                }
            } catch (_) {
                newErrors.website_url = 'Please enter a valid URL';
            }
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (entry?.categories && entry.categories.length > 0) {
            initialCategoriesRef.current = entry.categories.filter(entryCategory =>
                categories.some((userCategory: any) => userCategory.id === entryCategory.id)
            ).map(cat => cat.id);
        }

        // Don't spread entry directly - assign each property with a default fallback
        setFormData({
            title: entry?.title || '',
            username: entry?.username || '',
            password: entry?.password || '',
            website_url: entry?.website_url || '',
            notes: entry?.notes || '',
            favourite: entry?.favourite || false,
            category_id: entry?.category_id || '',
            category_ids: [],
            primary_category_id: selectedCategory?.id || '',
            isSharedUpdate: isSharedPassword || !!entry?.shared_key,
            sharedKey: entry?.shared_key || undefined,
            auto_login: entry?.auto_login || false,
            password_strength: entry?.password_strength || '',
            favicon_url: entry?.favicon_url || '',
            remove_category_ids: [],
        });

        setIsFormShared(isSharedPassword || !!entry?.shared_key);

        // if (isOpen && !isSharedPassword) {
        //     refreshCategories();
        // }
    }, [entry, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            // Reset the initial categories ref when modal closes
            initialCategoriesRef.current = [];
            setFormData(prev => ({
                ...prev,
                category_ids: [],
                remove_category_ids: []
            }));
        }
    }, [isOpen]);

    // Handle category creation
    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategoryLoading(true);
        try {
            const newCategory = await createCategory({
                name: newCategoryName,
                workspace_id: activeWorkspace?.id || undefined
            });
            await refreshCategories();

            // Add the new category to selected options
            const newOption: Option = {
                label: newCategoryName,
                value: newCategory.id,
                isShared: false,
                sharedKey: undefined
            };
            setSelectedOptions([...selectedOptions, newOption]);

            // Reset the create form
            setNewCategoryName('');
            setIsCreatingCategory(false);
            toast.success('Vault created successfully');
        } catch (error) {
            console.error('Error creating vault:', error);
            toast.error('Failed to create vault');
        } finally {
            setCreatingCategoryLoading(false);
        }
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        if (value === "create_new") {
            setIsCreatingCategory(true);
            return;
        }

        const category = categories.find(cat => cat.id === value);
        setSelectedCategory(category || null);

        setFormData(prev => ({
            ...prev,
            category_id: value,
            isSharedUpdate: category?.is_shared || false,
            sharedKey: category?.is_shared && category.shared_key ?
                decryptKeyData(category.shared_key) : undefined
        }));
    };

    // Handle multi-select change
    const handleMultiSelectChange = (selected: Option[]) => {
        const newSelectedIds = selected.map(opt => opt.value);

        // Compare with initial categories to determine which ones were removed
        const removedCategoryIds = initialCategoriesRef.current.filter(
            id => !newSelectedIds.includes(id)
        );

        console.log('Initial Categories:', initialCategoriesRef.current);
        console.log('New Selected IDs:', newSelectedIds);
        console.log('All Removed Category IDs:', removedCategoryIds);

        setSelectedOptions(selected);

        // Update form data with category information
        const categoryIds = selected.map(option => option.value);

        // Check if any selected category is shared
        const hasSharedCategory = selected.some(opt => opt.isShared);
        const sharedOption = selected.find(opt => opt.isShared);

        // Prioritize entry's shared_key if it exists
        const newSharedKey = entry?.shared_key ||
            (hasSharedCategory && sharedOption?.sharedKey ?
                decryptKeyData(sharedOption.sharedKey) :
                undefined);

        setFormData(prev => ({
            ...prev,
            category_ids: categoryIds,
            remove_category_ids: removedCategoryIds,
            isSharedUpdate: hasSharedCategory || isFormShared,
            sharedKey: newSharedKey
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        const newValue = isCheckbox ? (e.target as HTMLInputElement).checked : value;

        // For form fields (no longer handling category_id since we use multi-select)
        setFormData(prev => {
            if (name === 'favourite' || name === 'auto_login') {
                return {
                    ...prev,
                    [name]: isCheckbox ? Boolean(newValue) : newValue
                };
            } else {
                return {
                    ...prev,
                    [name]: typeof newValue === 'string' ? newValue : prev[name as keyof typeof prev]
                };
            }
        });

        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        const workspaceId = activeWorkspace?.id || team_id || undefined;

        if (entry?.is_owner && !entry?.shared_key) {
            formData.sharedKey = secureStore.getVaultKey();
        }

        // Safely check if shared_key exists before trying to decrypt it
        let isVaultOwner = false;

        if (entry?.shared_key) {
            try {
                isVaultOwner = entry.shared_key === secureStore.getVaultKey();
            } catch (error) {
                console.error('Error decrypting shared key:', error);
                // Continue with isVaultOwner as false
            }
        }

        try {
            // Calculate password strength
            const passwordStrength = calculatePasswordStrength(formData.password, formData.username);

            // Get category IDs and primary category from selected categories
            const categoryIds = selectedOptions.map(opt => opt.value);

            console.log("shared key", entry);

            // const primaryCategoryId = selectedOptions.find(opt => opt.isShared)?.value || categoryIds[0] || '';

            const updatedEntry: ICreatePasswordEntry = {
                title: formData.title,
                username: formData.username,
                password: formData.password,
                website_url: formData.website_url,
                notes: formData.notes,
                favourite: formData.favourite,
                category_ids: categoryIds,
                remove_category_ids: formData.remove_category_ids,
                auto_login: autoLogin,
                sharedKey: formData.sharedKey,
                isSharedUpdate: isVaultOwner ? isVaultOwner : formData.isSharedUpdate,
                itemType: isVaultOwner || formData.isSharedUpdate ? 'category' : entry?.itemType || 'password',
                favicon_url: formData.favicon_url,
                password_strength: passwordStrength,
                workspace_id: workspaceId
            };

            console.log("updatedEntry", updatedEntry);

            await VaultService.updateEntry(entry?.id || '', updatedEntry, workspaceId);

            // Refresh entries in multiple ways to ensure consistency
            await refreshEntries();

            if (onSuccessfulUpdate) {
                await onSuccessfulUpdate();
            }

            toast.success('Password updated successfully');
            onClose();
        } catch (error) {
            console.error('Failed to update entry:', error);
            toast.error('Failed to update entry');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Enhanced Backdrop */}
            <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-3xl w-full max-w-7xl mx-4 max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
                    {/* Modal Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                                    <KeyIcon className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold">
                                        {isReadOnly && isV1Password 
                                            ? 'View Password (Migration Required)' 
                                            : isReadOnly 
                                            ? 'View Shared Password' 
                                            : 'Edit Password Entry'}
                                    </h3>
                                    <p className="text-primary-100 text-sm">
                                        {isV1Password 
                                            ? 'This password uses V1 encryption and requires migration to edit' 
                                            : isReadOnly 
                                            ? 'Review password details' 
                                            : 'Update your password entry with enhanced security'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-2xl transition-all duration-200"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* V1 Migration Warning Banner */}
                    {entry?.crypto_version === 1 && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-8 mt-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <ShieldCheckIcon className="h-6 w-6 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        Security Migration Required
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <p>
                                            This password is using an older encryption method (V1). To edit this password, 
                                            you must first complete the security migration to upgrade all your passwords to 
                                            the new RSA-4096 encryption (V2).
                                        </p>
                                        <p className="mt-2">
                                            You can view this password, but editing is disabled until migration is complete.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal Content */}
                    <form onSubmit={handleSubmit} className="flex flex-col h-full" autoComplete="off">
                        <div className="flex-1 overflow-auto max-h-[calc(95vh-140px)] bg-gray-50">
                            <div className="p-8">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                    {/* Left Column - Basic Info & Vaults */}
                                    <div className="lg:col-span-1">
                                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-fit">
                                            <div className="flex items-center space-x-3 mb-6">
                                                <div className="p-2 bg-primary-100 rounded-xl">
                                                    <DocumentTextIcon className="w-5 h-5 text-primary-600" />
                                                </div>
                                                <h4 className="text-lg font-semibold text-gray-900">Entry Details</h4>
                                            </div>

                                            <div className="space-y-6">
                                                {/* Entry Name */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Entry Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="title"
                                                            value={formData.title}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                                } ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                                            placeholder="Enter entry name"
                                                        />
                                                        <SparklesIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                    </div>
                                                    {errors.title && (
                                                        <p className="mt-2 text-sm text-red-600 flex items-center">
                                                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                                                            {errors.title}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Vaults Section */}
                                                {(entry?.is_owner || role_id === 1 || role_id === 2) && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Select Vaults
                                                        </label>
                                                        <div className="space-y-2 sm:space-y-3">
                                                            <MultiSelect
                                                                options={categoryOptions}
                                                                value={selectedOptions}
                                                                onChange={handleMultiSelectChange}
                                                                labelledBy="Select vaults"
                                                                disabled={isReadOnly}
                                                                hasSelectAll={false}
                                                                className="w-full"
                                                                ClearSelectedIcon={null}
                                                                debounceDuration={0}
                                                            />

                                                            {/* Create New Vault */}
                                                            {!isReadOnly && (
                                                                <div>
                                                                    {!isCreatingCategory ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setIsCreatingCategory(true)}
                                                                            className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-xl transition-all duration-200"
                                                                        >
                                                                            <PlusIcon className="w-4 h-4 mr-2" />
                                                                            Create New Vault
                                                                        </button>
                                                                    ) : (
                                                                        <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
                                                                            <input
                                                                                type="text"
                                                                                value={newCategoryName}
                                                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                                placeholder="Enter vault name"
                                                                                autoFocus
                                                                            />
                                                                            <div className="flex space-x-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleCreateCategory}
                                                                                    disabled={creatingCategoryLoading || !newCategoryName.trim()}
                                                                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${creatingCategoryLoading || !newCategoryName.trim()
                                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                                        : 'bg-primary-600 text-white hover:bg-primary-700'
                                                                                        }`}
                                                                                >
                                                                                    {creatingCategoryLoading ? 'Creating...' : 'Create'}
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setIsCreatingCategory(false);
                                                                                        setNewCategoryName('');
                                                                                    }}
                                                                                    className="flex-1 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Favorite Toggle */}
                                                {entry?.is_owner && (
                                                    <div className="flex items-center p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-100">
                                                        <input
                                                            type="checkbox"
                                                            id="favourite"
                                                            name="favourite"
                                                            checked={formData.favourite}
                                                            onChange={(e) => setFormData({ ...formData, favourite: e.target.checked })}
                                                            className="h-5 w-5 text-red-500 focus:ring-red-400 border-red-300 rounded"
                                                            disabled={isReadOnly}
                                                        />
                                                        <label htmlFor="favourite" className="ml-3 flex items-center text-sm font-medium text-red-700">
                                                            {formData.favourite ? (
                                                                <HeartSolidIcon className="w-5 h-5 text-red-500 mr-2" />
                                                            ) : (
                                                                <HeartIcon className="w-5 h-5 text-red-400 mr-2" />
                                                            )}
                                                            Add to favorites
                                                        </label>
                                                    </div>
                                                )}

                                                {/* Auto-Login Toggle */}
                                                {entry?.is_owner && (
                                                    <div className="flex items-center p-4 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-xl border border-primary-100">
                                                        <input
                                                            type="checkbox"
                                                            id="auto_login"
                                                            name="auto_login"
                                                            checked={autoLogin}
                                                            onChange={(e) => setAutoLogin(e.target.checked)}
                                                            className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-primary-300 rounded"
                                                            disabled={isReadOnly}
                                                        />
                                                        <label htmlFor="auto_login" className="ml-3 flex flex-col">
                                                            <span className="flex items-center text-sm font-medium text-primary-700">
                                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                                                </svg>
                                                                Enable Auto-Login
                                                            </span>
                                                            <span className="text-xs text-primary-600 ml-7">Automatically login when visiting this website</span>
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Middle Column - Account Information */}
                                    <div className="lg:col-span-1">
                                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-fit">
                                            <div className="flex items-center space-x-3 mb-6">
                                                <div className="p-2 bg-blue-100 rounded-xl">
                                                    <UserIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <h4 className="text-lg font-semibold text-gray-900">Account Details</h4>
                                            </div>

                                            <div className="space-y-6">
                                                {/* Username */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Username
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="username"
                                                            value={formData.username}
                                                            onChange={handleChange}
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                                } ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                                            placeholder="Enter username"
                                                        />
                                                        <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                    </div>
                                                    {errors.username && (
                                                        <p className="mt-2 text-sm text-red-600 flex items-center">
                                                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                                                            {errors.username}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Password */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Password
                                                    </label>
                                                    <div className="relative">
                                                        <Input
                                                            name="password"
                                                            type={showPassword ? "text" : "password"}
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            autoComplete="new-password"
                                                            className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                                } ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                                            required
                                                            disabled={isReadOnly}
                                                            showPasswordToggle
                                                            placeholder="Enter password"
                                                        />
                                                        <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                    </div>
                                                    {errors.password && (
                                                        <p className="mt-2 text-sm text-red-600 flex items-center">
                                                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                                                            {errors.password}
                                                        </p>
                                                    )}
                                                    <div className="mt-3">
                                                        <PasswordStrengthMeter
                                                            password={formData.password}
                                                            email={formData.username}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Website URL */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Website URL
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="website_url"
                                                            value={formData.website_url}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${errors.website_url ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                                } ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                                            placeholder="https://example.com"
                                                        />
                                                        <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                    </div>
                                                    {errors.website_url && (
                                                        <p className="mt-2 text-sm text-red-600 flex items-center">
                                                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                                                            {errors.website_url}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Favicon URL */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Favicon URL
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="favicon_url"
                                                            value={formData.favicon_url}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                }`}
                                                            placeholder="https://example.com/favicon.ico"
                                                        />
                                                        <SparklesIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Additional Information */}
                                    <div className="lg:col-span-1">
                                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-fit">
                                            <div className="flex items-center space-x-3 mb-6">
                                                <div className="p-2 bg-green-100 rounded-xl">
                                                    <DocumentTextIcon className="w-5 h-5 text-green-600" />
                                                </div>
                                                <h4 className="text-lg font-semibold text-gray-900">Additional Info</h4>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Notes
                                                </label>
                                                <textarea
                                                    name="notes"
                                                    value={formData.notes}
                                                    onChange={handleChange}
                                                    rows={8}
                                                    disabled={isReadOnly}
                                                    className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none ${isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                        }`}
                                                    placeholder="Add any additional notes here..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Footer */}
                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 text-sm text-gray-500">
                                    <ShieldCheckIcon className="w-5 h-5 text-green-500" />
                                    <span>All data is encrypted and secure</span>
                                </div>
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
                                    >
                                        {isReadOnly ? 'Close' : 'Cancel'}
                                    </button>
                                    {!isReadOnly && (
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`px-8 py-3 rounded-xl text-sm font-medium text-white transition-all duration-200 ${loading
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                                                }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Saving...
                                                </div>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditPasswordModal;